/**
 * +quests: speech bubbles over the side-quest NPCs when the hero walks past ("Eleven socks.
 * ELEVEN."). Same look as the party chat bubbles (hud/chat: .chat-bubble), positioned by
 * projecting the NPC's head each frame while one is showing; one bubble per NPC.
 */
import type { Vec2 } from '@keystone/sim';
import { el } from '../dom';
import { BUBBLE_MS, bubbleOpacity } from '../chat/model';
import './quests.css';

export interface BarkProjector {
  project(p: Vec2, height: number): { x: number; y: number; visible: boolean };
}

interface Bark {
  root: HTMLElement;
  pos: Vec2;
  height: number;
  since: number;
}

/** Bubbles stay a little longer than chat (they are read while walking). */
export const BARK_MS = BUBBLE_MS + 1500;

export class BarkBubbles {
  private readonly layer: HTMLElement;
  private readonly barks = new Map<string, Bark>();
  private raf = 0;

  constructor(parent: HTMLElement, private readonly projector: () => BarkProjector | null) {
    this.layer = el('div', 'fx-barks', parent);
  }

  show(npcId: string, text: string, pos: Vec2, height: number): void {
    let b = this.barks.get(npcId);
    if (!b) {
      const root = el('div', 'chat-bubble fx-bark', this.layer);
      el('div', 'chat-bubble-body', root);
      b = { root, pos, height, since: 0 };
      this.barks.set(npcId, b);
    }
    b.since = performance.now();
    b.pos = pos;
    b.height = height;
    (b.root.firstElementChild as HTMLElement).textContent = text;
    if (!this.raf) this.raf = requestAnimationFrame(this.frame);
  }

  /** Bubbles showing right now (tests / debug). */
  get count(): number {
    return this.barks.size;
  }

  private readonly frame = (): void => {
    this.raf = 0;
    const now = performance.now();
    const pr = this.projector();
    for (const [id, b] of this.barks) {
      // Barks last BARK_MS: shift the chat bubble's fade to the end of that.
      const op = bubbleOpacity(now - (BARK_MS - BUBBLE_MS), b.since);
      if (op <= 0 || !pr) {
        b.root.remove();
        this.barks.delete(id);
        continue;
      }
      const sp = pr.project(b.pos, b.height);
      b.root.classList.toggle('off', !sp.visible);
      b.root.style.transform = `translate3d(${Math.round(sp.x)}px,${Math.round(sp.y)}px,0)`;
      b.root.style.opacity = op.toFixed(2);
    }
    if (this.barks.size) this.raf = requestAnimationFrame(this.frame);
  };

  clear(): void {
    for (const b of this.barks.values()) b.root.remove();
    this.barks.clear();
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.layer.remove();
  }
}
